// src/pages/automations/ViewAutomation.tsx

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../app/store';
import { FiChevronLeft } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import HierarchyGraphFlow from './HierarchyGraphFlow';
import ChartComponent from './ChartComponent';
import Cards from './Cards';
// import TableComponent from './TableComponent';
import { transformApiAutomationToFrontend } from './automationGraphMapper';
import { getAuthContext } from '../../utils/jwt';
import { updateAutomationData, saveAutomation } from '../../features/automation/automationSlice';
import { v4 as uuidv4 } from 'uuid'; // Make sure to install this: npm install uuid @types/uuid
import { BASE_URL } from '../../utils/constants';

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const ViewAutomation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const [isPaused, setIsPaused] = useState(false);
  const [showFlow, setShowFlow] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadedAutomation, setLoadedAutomation] = useState<any>(null);
  // const [timeRange, setTimeRange] = useState('24h');
  const [isToggling, setIsToggling] = useState(false);

  // Extracts the automation ID from the URL pathname
  const automationId = id || location.pathname.split('/').pop() || '';

  // Retrieves automation data from Redux store based on the extracted ID
  const automation = useSelector((state: RootState) =>
    state.automation.savedAutomations.find((a) => a.id === automationId)
  );

  // ------------------------------------------------------------------
  // Load automation from API
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!automationId) return;

    const fetchAutomation = async () => {
      setLoading(true);

      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        navigate('/automations');
        setLoading(false);
        return;
      }


      try {

        const response = await fetch(
          `${BASE_URL}/api/v1/automations/${automationId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load automation: ${response.status}`);
        }

        const apiData = await response.json();

        // Set isPaused based on the status from API
        // Status "PAUSED" should set the toggle to ON (true)
        const isPausedStatus = apiData.status === 'PAUSED';
        setIsPaused(isPausedStatus);

        const transformed = transformApiAutomationToFrontend(apiData);

        // Update Redux store
        dispatch(
          updateAutomationData({
            automationFlowName: transformed.name,
            trigger: transformed.trigger,
            conditions: transformed.conditions,
          })
        );

        // Save to Redux
        dispatch(
          saveAutomation({
            id: transformed.id,
            name: transformed.name,
            trigger: transformed.trigger,
            status: transformed.status as 'draft' | 'published',
            nodes: transformed.nodes,
            edges: transformed.edges,
          })
        );

        setLoadedAutomation({
          id: transformed.id,
          name: transformed.name,
          trigger: transformed.trigger,
          status: transformed.status,
          nodes: transformed.nodes,
          edges: transformed.edges,
          conditions: transformed.conditions,
        });


      } catch (err: any) {
        // console.error('❌ Failed to load automation', err);
        toast.error(err.message || 'Automation not found or failed to load', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        navigate('/automations');
      } finally {
        setLoading(false);
      }
    };

    fetchAutomation();
  }, [automationId, dispatch, navigate]);

  // Use loadedAutomation from API or fallback to Redux data
  const automationData = loadedAutomation || automation || {
    id: '',
    name: 'Untitled Automation',
    description: 'No description available',
    trigger: '',
    status: 'draft',
    nodes: [],
    edges: [],
    timestamp: '-',
  };

  // Mock data for charts and tables (to be replaced with actual data in production)
  const mockData = {
    tableData: [
      {
        emailName: 'Welcome Email',
        recipients: 15000,
        openRate: '14.1%',
        clickRate: '6.8%',
      },
      {
        emailName: 'Product Introduction Email',
        recipients: 15000,
        openRate: '15.6%',
        clickRate: '7.6%',
      },
      {
        emailName: 'Final Email',
        recipients: 15000,
        openRate: '14.1%',
        clickRate: '6.8%',
      },
    ],
    recipientsData: [
      {
        recipient: 'John Doe',
        tags: 'Political',
        triggerTimestamp: '15-08-2025, 10:00am',
        checkpoint: 'Welcome Email',
        currentStatus: 'Clicked CTA',
      },
      {
        recipient: 'John Doe',
        tags: 'Political',
        triggerTimestamp: '15-08-2025, 10:00am',
        checkpoint: 'Welcome Email',
        currentStatus: 'Not opened',
      },
      {
        recipient: 'John Doe',
        tags: 'Sports',
        triggerTimestamp: '15-08-2025, 10:00am',
        checkpoint: 'Welcome Email',
        currentStatus: 'Open',
      },
      {
        recipient: 'John Doe',
        tags: 'Sports',
        triggerTimestamp: '15-08-2025, 10:00am',
        checkpoint: 'Welcome Email',
        currentStatus: 'Not received',
      },
      {
        recipient: 'John Doe',
        tags: 'Entertainment',
        triggerTimestamp: '15-08-2025, 10:00am',
        checkpoint: 'Welcome Email',
        currentStatus: 'Spam',
      },
      {
        recipient: 'John Doe',
        tags: 'Entertainment',
        triggerTimestamp: '15-08-2025, 10:00am',
        checkpoint: 'Welcome Email',
        currentStatus: 'Not opened',
      },
    ],
    subscribed: 1250,
    conversion: 320,
    revenueGenerated: '$15,500',
    engagementScore: '85%',
  };

  // ------------------------------------------------------------------
  // Handle toggle change - calls pause/resume API
  // ------------------------------------------------------------------
  const handlePauseToggle = async () => {
    // Prevent multiple clicks while toggling
    if (isToggling) return;

    const newPausedState = !isPaused;

    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    setIsToggling(true);

    try {
      let endpoint = '';
      let method = 'POST';
      let body = null;

      if (newPausedState) {
        // If going to PAUSED state, call pause API
        endpoint = `${BASE_URL}/api/v1/automations/${automationId}/pause?reason=i want pause&cancelRunning=false`;
      } else {
        // If going to ACTIVE state, call resume API
        endpoint = `${BASE_URL}/api/v1/automations/${automationId}/resume`;
      }

      // Generate Idempotency-Key
      const idempotencyKey = uuidv4();

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Idempotency-Key': idempotencyKey,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${newPausedState ? 'pause' : 'resume'} automation: ${response.status}`);
      }

      // Update the local state only after successful API call
      setIsPaused(newPausedState);

      // Show success toast
      toast.success(`Automation ${newPausedState ? 'paused' : 'resumed'} successfully!`, {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      });

      // Optionally refresh the automation data to get the latest status
      // You can either refetch or update the status in the loadedAutomation state
      setLoadedAutomation((prev: any) => ({
        ...prev,
        status: newPausedState ? 'PAUSED' : 'ACTIVE'
      }));

    } catch (err: any) {
      // console.error(`❌ Failed to ${newPausedState ? 'pause' : 'resume'} automation`, err);
      toast.error(err.message || `Failed to ${newPausedState ? 'pause' : 'resume'} automation`, {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      // Don't update the state if API call failed
    } finally {
      setIsToggling(false);
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-500">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <span>Loading automation...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2">
      {/* Back Button */}
      <button
        onClick={() => navigate('/automations')}
        className="flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold"
        style={{ color: '#00899E', fontSize: '14px' }}
      >
        <FiChevronLeftComponent className="mr-1" /> Back to Automations
      </button>

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-[20px] font-bold text-[#3F5265]">
            View Automation
          </h2>
          <p className="text-[#657584] text-[16px]">
            Automation performance and analytics
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[#657584] font-medium text-[12px] mr-2">
                Pause Automation
              </p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPaused}
                onChange={handlePauseToggle}
                disabled={isToggling}
                className="sr-only"
              />
              <div
                className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${isPaused ? 'bg-[#00A4BD]' : 'bg-gray-300'
                  } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''} relative`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform duration-200 ease-in-out ${isPaused ? 'left-6' : 'left-0.5'
                    }`}
                ></div>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Flow View */}
      {showFlow ? (
        <div className="bg-[#e7eef3] rounded-md border border-gray-200 shadow-sm p-4">
          <HierarchyGraphFlow
            initialNodes={automationData.nodes.map((node: any) => ({
              ...node,
              delayType:
                node.delayType === 'fixed' || node.delayType === 'specific'
                  ? node.delayType
                  : undefined,
            }))}
            initialEdges={automationData.edges}
            automation={{
              trigger: automationData.trigger
            }}
            isViewMode={true}
          />
        </div>
      ) : (
        <div className="bg-white p-6 rounded-md shadow-sm border">
          <h2 className="text-[18px] font-semibold text-[#3F5265]">
            {automationData.name}
          </h2>
          <p className="text-[#657584] mb-6 text-[16px]">
            Lorem ipsum dolor sit amet consectetur adipisicing elit.
          </p>
          {/* Cards */}
          <Cards
            subscribed={mockData.subscribed}
            conversion={mockData.conversion}
            revenueGenerated={mockData.revenueGenerated}
            engagementScore={mockData.engagementScore}
          />

          {/* Chart */}
          <ChartComponent />
        </div>
      )}

      {/* Table */}
      {/* <TableComponent /> */}
      <ToastContainer />
    </div>
  );
};

export default ViewAutomation;